// =============================================================================
// Child theme entry — Shenton Dental mockup behaviors.
// Ported 1:1 from the <script> in shenton-dental-v3.html, adapted from SPA
// view-switching to real multi-page navigation.
// =============================================================================

import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';

// ---------------------------------------------------------------------------
// Header navigation — the mobile "hamburger" overlay is handled natively by the
// core Navigation block. Here we only mark the current section's link active,
// including sub-pages (e.g. /services/whitening/ highlights "Services"), which
// the block's exact-URL aria-current match doesn't cover.
// ---------------------------------------------------------------------------
(function setActiveNav() {
  const segments = window.location.pathname.split('/').filter(Boolean);
  const name = segments.length > 0 ? segments[0] : 'home';

  document
    .querySelectorAll('.primary-nav .wp-block-navigation-item')
    .forEach((item) => {
      const link = item.querySelector('a');
      const hrefSegments = ((link && link.getAttribute('href')) || '')
        .replace(/^https?:\/\/[^/]+/, '')
        .split('/')
        .filter(Boolean);
      const linkName = hrefSegments.length > 0 ? hrefSegments[0] : 'home';
      item.classList.toggle('is-current', linkName === name);
    });
})();

// ---------------------------------------------------------------------------
// Availability helpers — shared logic behind every flatpickr-backed date
// field on the site, parametrized over one config object so each field can
// be wired to its OWN availability data:
//   - #booking-form / #hero-booking-form -> window.shentonBooking
//   - #shenton_pickup_date (checkout-pickup.php) -> window.shentonPickup
// These are separate settings server-side (inc/pickup-settings.php) — pickup
// only mirrors booking's hours while its "Use the same hours as Booking"
// toggle is on. Closed weekdays / blackout dates render greyed out and
// un-clickable right in the calendar, instead of only erroring after the
// date is chosen.
// ---------------------------------------------------------------------------
function createAvailabilityHelpers(cfg) {
  cfg = cfg || {};
  const blackout = Array.isArray(cfg.blackoutDates) ? cfg.blackoutDates : [];
  const closedWeekdays = Array.isArray(cfg.closedWeekdays) ? cfg.closedWeekdays.map(Number) : [];
  const closedMsg = cfg.closedMessage || 'The clinic is closed on this date. Please pick another day.';
  const noSlotsMsg = cfg.noSlotsMessage || 'No slots are available on this date. Please pick another day.';
  const slotsByWeekday = cfg.slotsByWeekday && typeof cfg.slotsByWeekday === 'object' ? cfg.slotsByWeekday : null;

  function weekdayOf(value) {
    const parts = value.split('-').map(Number);
    return new Date(parts[0], (parts[1] || 1) - 1, parts[2] || 1).getDay();
  }

  // Local YYYY-MM-DD for a flatpickr Date object (avoids the UTC shift of
  // toISOString() for dates near midnight in timezones ahead of UTC).
  function toYMD(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  // Slot labels bookable on the given YYYY-MM-DD date, or null when unknown.
  function slotsForDate(value) {
    if (!slotsByWeekday || !value) return null;
    const list = slotsByWeekday[weekdayOf(value)];
    return Array.isArray(list) ? list : [];
  }

  // Returns an error string if the date is not bookable, otherwise ''.
  function dateError(value) {
    if (!value) return '';
    if (cfg.minDate && value < cfg.minDate) return 'Please choose a future date.';
    if (closedWeekdays.includes(weekdayOf(value))) return closedMsg;
    if (blackout.includes(value)) return closedMsg;
    const slots = slotsForDate(value);
    if (slots && slots.length === 0) return noSlotsMsg;
    return '';
  }

  // Rebuild a <select name="time"> to the slots bookable on `dateValue`,
  // keeping the current choice when it is still offered.
  function syncTimeOptions(timeSelect, dateValue) {
    if (!timeSelect) return;
    const slots = slotsForDate(dateValue);
    if (!slots) return; // no map available — leave the server-rendered list
    const previous = timeSelect.value;
    const placeholder = timeSelect.querySelector('option[value=""]');
    timeSelect.innerHTML = '';
    if (placeholder) timeSelect.appendChild(placeholder);
    else {
      const opt = document.createElement('option');
      opt.value = '';
      opt.textContent = 'Select time…';
      timeSelect.appendChild(opt);
    }
    slots.forEach((slot) => {
      const opt = document.createElement('option');
      opt.value = slot;
      opt.textContent = slot;
      timeSelect.appendChild(opt);
    });
    timeSelect.value = slots.includes(previous) ? previous : '';
  }

  // Attach a flatpickr calendar to `dateInput`: closed weekdays and blackout
  // dates render greyed out and un-clickable, min date is today, and
  // choosing a date re-narrows `timeSelect` + surfaces `dateError()` via
  // setCustomValidity/reportValidity (and `errorEl`, when given a visible
  // error line to fill in).
  function initDatePicker(dateInput, timeSelect, errorEl) {
    if (!dateInput) return;

    function applyError(value) {
      const err = dateError(value);
      dateInput.setCustomValidity(err);
      if (errorEl) {
        errorEl.textContent = err;
        errorEl.hidden = !err;
      } else if (err) {
        dateInput.reportValidity();
      }
      return err;
    }

    flatpickr(dateInput, {
      dateFormat: 'Y-m-d',
      minDate: cfg.minDate || 'today',
      disable: [(date) => closedWeekdays.includes(date.getDay()) || blackout.includes(toYMD(date))],
      onChange(selectedDates, dateStr) {
        applyError(dateStr);
        syncTimeOptions(timeSelect, dateStr);
      },
    });
  }

  return { cfg, dateError, syncTimeOptions, initDatePicker };
}

// ---------------------------------------------------------------------------
// Booking forms — the dedicated booking page (#booking-form) and the hero
// quick-book banner (#hero-booking-form) submit to admin-ajax (action:
// shenton_submit_booking) and are stored as a shenton_booking post.
// ---------------------------------------------------------------------------
(function initBookingForms() {
  const { cfg, dateError, initDatePicker } = createAvailabilityHelpers(window.shentonBooking);

  document.querySelectorAll('#booking-form, #hero-booking-form').forEach((form) => {
    const isHero = form.id === 'hero-booking-form';

    // --- Symptoms toggle (only when service = Others) ---
    const serviceSel = form.querySelector('select[name="service"]');
    const symptomsWrap = form.querySelector('.symptoms-wrap');
    if (serviceSel && symptomsWrap) {
      serviceSel.addEventListener('change', () => {
        symptomsWrap.style.display = serviceSel.value === 'Others' ? '' : 'none';
      });
    }

    // --- Date field: flatpickr calendar, closed days greyed out ---
    const dateInput = form.querySelector('input[name="date"]');
    const timeSelect = form.querySelector('select[name="time"]');
    initDatePicker(dateInput, timeSelect);

    // --- Message + success/reset elements (per-form) ---
    let msg = form.querySelector('.hero-booking-msg') || form.querySelector('.booking-msg');
    if (!msg) {
      msg = document.createElement('p');
      msg.className = 'booking-msg';
      msg.hidden = true;
      form.appendChild(msg);
    }
    const submitBtn =
      form.querySelector('.hero-booking-submit') || form.querySelector('button[type="submit"]');
    const successEl = isHero
      ? document.querySelector('.hero-booking-success')
      : document.getElementById('booking-success');

    function setFormVisible(visible) {
      if (isHero) {
        form.hidden = !visible;
      } else {
        form.style.display = visible ? '' : 'none';
      }
    }
    function setSuccessVisible(visible) {
      if (!successEl) return;
      if (isHero) successEl.hidden = !visible;
      else successEl.style.display = visible ? '' : 'none';
    }

    // Booking page "Book Another Appointment" button.
    const resetBtn = document.getElementById('booking-reset-btn');
    if (resetBtn && !isHero) {
      resetBtn.addEventListener('click', () => {
        form.reset();
        if (symptomsWrap) symptomsWrap.style.display = 'none';
        setFormVisible(true);
        setSuccessVisible(false);
      });
    }

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const de = dateInput ? dateError(dateInput.value) : '';
      if (de) {
        if (dateInput) {
          dateInput.setCustomValidity(de);
          dateInput.reportValidity();
        }
        msg.textContent = de;
        msg.hidden = false;
        return;
      }

      const originalLabel = submitBtn ? submitBtn.textContent : '';
      const body = new FormData(form);
      body.append('action', 'shenton_submit_booking');
      body.append('nonce', cfg.nonce || '');

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Sending…';
      }
      msg.hidden = true;

      try {
        const res = await fetch(cfg.ajaxUrl || '/wp-admin/admin-ajax.php', {
          method: 'POST',
          body,
          credentials: 'same-origin',
        });
        const json = await res.json();
        if (!res.ok || !json.success) {
          throw new Error((json.data && json.data.message) || 'Something went wrong. Please try again.');
        }
        setFormVisible(false);
        setSuccessVisible(true);
      } catch (err) {
        msg.textContent = err.message;
        msg.hidden = false;
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = originalLabel;
        }
      }
    });
  });
})();

// ---------------------------------------------------------------------------
// Checkout pickup date/time (checkout-pickup.php) — same flatpickr wiring as
// the booking forms above, but against its own availability config
// (window.shentonPickup), which may differ from booking's once the
// dashboard's "Use the same hours as Booking" toggle is switched off.
// ---------------------------------------------------------------------------
(function initPickupDatePicker() {
  const pickupDate = document.getElementById('shenton_pickup_date');
  if (!pickupDate) return;

  const { initDatePicker } = createAvailabilityHelpers(window.shentonPickup);
  const pickupTime = document.getElementById('shenton_pickup_time');
  const pickupError = document.getElementById('shenton-pickup-error');
  initDatePicker(pickupDate, pickupTime, pickupError);
})();

// ---------------------------------------------------------------------------
// Doctor bio "Show more" (home-team block) — long bios render clamped to a
// few lines with a toggle button instead of stretching the card; see
// .team-bio-wrap in shenton.scss and the has-toggle flag in render.php.
// ---------------------------------------------------------------------------
document.querySelectorAll('.team-bio-toggle').forEach((btn) => {
  btn.addEventListener('click', () => {
    const wrap = btn.closest('.team-bio-wrap');
    if (!wrap) return;
    const expanded = wrap.classList.toggle('is-expanded');
    btn.textContent = expanded ? 'Show less' : 'Show more';
  });
});

// ---------------------------------------------------------------------------
// Govt Subsidy logo slider — arrows move the scroll-snap track one logo at a
// time and disable at either end; arrows hide when every logo already fits.
// ---------------------------------------------------------------------------
(function initSubsidySliders() {
  document.querySelectorAll('[data-subsidy-slider]').forEach((slider) => {
    const track = slider.querySelector('.subsidy-track');
    const prev = slider.querySelector('.subsidy-arrow--prev');
    const next = slider.querySelector('.subsidy-arrow--next');
    if (!track || !prev || !next) return;

    function update() {
      const max = track.scrollWidth - track.clientWidth;
      slider.classList.toggle('is-static', max <= 1);
      prev.disabled = track.scrollLeft <= 1;
      next.disabled = track.scrollLeft >= max - 1;
    }

    function page(dir) {
      const first = track.querySelector('.subsidy-logo');
      if (!first) return;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      track.scrollBy({ left: dir * (first.offsetWidth + gap), behavior: 'smooth' });
    }

    prev.addEventListener('click', () => page(-1));
    next.addEventListener('click', () => page(1));
    track.addEventListener('scroll', update, { passive: true });
    track.querySelectorAll('img').forEach((img) => img.addEventListener('load', update));
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
    else window.addEventListener('resize', update);
    update();
  });
})();

// ---------------------------------------------------------------------------
// Footer newsletter — client-side subscribe confirmation.
// ---------------------------------------------------------------------------
(function initFooterNewsletter() {
  const form = document.getElementById('footer-newsletter-form');
  if (!form) return;

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    form.hidden = true;
    const msg = document.getElementById('footer-newsletter-msg');
    if (msg) msg.hidden = false;
  });
})();

// ---------------------------------------------------------------------------
// Shop + cart drawer (WooCommerce)
// ---------------------------------------------------------------------------
const cartOverlay = document.getElementById('cart-overlay');
const cartDrawer = document.getElementById('cart-drawer');

function openCart() {
  if (cartOverlay) cartOverlay.classList.add('open');
  if (cartDrawer) cartDrawer.classList.add('open');
}
function closeCart() {
  if (cartOverlay) cartOverlay.classList.remove('open');
  if (cartDrawer) cartDrawer.classList.remove('open');
}

function updateCartBadge() {
  const badge = document.getElementById('cart-badge');
  if (!badge) return;
  let count = 0;
  document.querySelectorAll('#cart-drawer .cart-line').forEach((line) => {
    count += parseInt(line.dataset.qty || '1', 10);
  });
  badge.style.display = count > 0 ? 'flex' : 'none';
  badge.textContent = count;
}

const cartOpenBtn = document.getElementById('cart-open-btn');
const cartCloseBtn = document.getElementById('cart-close-btn');
if (cartOpenBtn) cartOpenBtn.addEventListener('click', openCart);
if (cartCloseBtn) cartCloseBtn.addEventListener('click', closeCart);
if (cartOverlay) cartOverlay.addEventListener('click', closeCart);

// WooCommerce fires these on the body after ajax add-to-cart and after the
// cart fragments refresh the mini cart markup.
if (window.jQuery) {
  window.jQuery(document.body).on('added_to_cart removed_from_cart wc_fragments_refreshed wc_fragments_loaded', () => {
    updateCartBadge();
  });
  window.jQuery(document.body).on('added_to_cart', () => {
    openCart();
  });
}

// The parent's AJAX add-to-cart goes through the Store API, which returns no
// fragments, so ask WC to re-render the drawer markup after each add.
document.body.addEventListener('wc-blocks_added_to_cart', () => {
  if (window.jQuery) window.jQuery(document.body).trigger('wc_fragment_refresh');
});
updateCartBadge();

/* ---------- Shop filter + sort toolbar ---------- */
(function initShopToolbar() {
  const grid = document.querySelector('.shop-grid');
  if (!grid) return;
  const chips = Array.from(document.querySelectorAll('.shop-filters .chip'));
  const priceChips = Array.from(document.querySelectorAll('.shop-prices .chip'));
  const sortSelect = document.getElementById('shop-sort');
  const countEl = document.querySelector('.shop-count');
  const cards = Array.from(grid.querySelectorAll('.product-card'));
  if (cards.length === 0) return;
  cards.forEach((card, i) => { card.dataset.index = String(i); });

  let activeCat = 'all';
  let activePrice = 'all';

  function priceMatches(price, mode) {
    switch (mode) {
      case 'under-15': return price < 15;
      case '15-60': return price >= 15 && price <= 60;
      case 'over-60': return price > 60;
      default: return true;
    }
  }

  function apply() {
    let visible = 0;
    cards.forEach((card) => {
      const cats = (card.dataset.cats || '').split(/\s+/).filter(Boolean);
      const catOk = activeCat === 'all' || cats.includes(activeCat);
      const priceOk = priceMatches(parseFloat(card.dataset.price) || 0, activePrice);
      const show = catOk && priceOk;
      card.style.display = show ? '' : 'none';
      if (show) visible += 1;
    });

    const mode = sortSelect ? sortSelect.value : 'featured';
    const sorted = [...cards].sort((a, b) => {
      switch (mode) {
        case 'price-asc':
          return parseFloat(a.dataset.price) - parseFloat(b.dataset.price);
        case 'price-desc':
          return parseFloat(b.dataset.price) - parseFloat(a.dataset.price);
        case 'name-asc':
          return (a.dataset.name || '').localeCompare(b.dataset.name || '');
        default:
          return parseInt(a.dataset.index, 10) - parseInt(b.dataset.index, 10);
      }
    });
    sorted.forEach((card) => grid.appendChild(card));

    if (countEl) {
      countEl.textContent = `${visible} ${visible === 1 ? 'product' : 'products'}`;
    }
  }

  chips.forEach((chip) => {
    chip.addEventListener('click', () => {
      activeCat = chip.dataset.cat || 'all';
      chips.forEach((c) => c.classList.toggle('is-active', c === chip));
      apply();
    });
  });
  priceChips.forEach((chip) => {
    chip.addEventListener('click', () => {
      activePrice = chip.dataset.price || 'all';
      priceChips.forEach((c) => c.classList.toggle('is-active', c === chip));
      apply();
    });
  });
  if (sortSelect) sortSelect.addEventListener('change', apply);
  apply();
})();
